import Carn from '@bluebeam/carnivore';
import { CursorTypes } from 'editor/components/Cursor';
import Markup from 'editor/components/Markup';
import Point from 'editor/geometry/Point';
import { pointInCircle } from 'editor/utils/hitDetection';

const CONTROL_POINT_RADIUS = 3;

/* Box Control Points */

// in x, y
export enum BoxControlPoint {
  None,
  LeftTop,
  CenterTop,
  RightTop,
  RightCenter,
  RightBottom,
  CenterBottom,
  LeftBottom,
  LeftCenter,
}

export function boxControlPointHit(mc: Markup, hit: Carn.Hit): BoxControlPoint {
  if (mc.mkMarkup) {
    const bbox = mc.mkMarkup.getBBox();
    const padding = 5;

    const l = bbox.left - padding;
    const t = bbox.top + padding;
    const r = bbox.right + padding;
    const b = bbox.bottom - padding;
    const h = (bbox.left + bbox.right) / 2; // horizonal center
    const v = (bbox.top + bbox.bottom) / 2; // vertical center

    // all in x, y
    const origins = [
      [l, t, BoxControlPoint.LeftTop],
      [h, t, BoxControlPoint.CenterTop],
      [r, t, BoxControlPoint.RightTop],
      [r, v, BoxControlPoint.RightCenter],
      [r, b, BoxControlPoint.RightBottom],
      [h, b, BoxControlPoint.CenterBottom],
      [l, b, BoxControlPoint.LeftBottom],
      [l, v, BoxControlPoint.LeftCenter],
    ];

    for (const [ox, oy, cp] of origins) {
      if (pointInCircle(ox, oy, hit.x, hit.y, CONTROL_POINT_RADIUS)) {
        return cp;
      }
    }
  }

  return BoxControlPoint.None;
}

export function controlPointHitDelta(
  mc: Markup,
  cp: BoxControlPoint,
  x: number,
  y: number
): { x: number; y: number } {
  const mkm = mc.mkMarkup;

  if (!mkm) {
    return { x: 0, y: 0 };
  }

  const [left, bottom, right, top] = mkm.getRect() as number[];

  let deltaX = 0;
  let deltaY = 0;

  switch (cp) {
    case BoxControlPoint.LeftTop:
      deltaX = left - x;
      deltaY = y - top;
      break;
    case BoxControlPoint.CenterTop:
      deltaY = y - top;
      break;
    case BoxControlPoint.RightTop:
      deltaX = x - right;
      deltaY = y - top;
      break;
    case BoxControlPoint.RightCenter:
      deltaX = x - right;
      break;
    case BoxControlPoint.RightBottom:
      deltaX = x - right;
      deltaY = y - bottom;
      break;
    case BoxControlPoint.CenterBottom:
      deltaY = y - bottom;
      break;
    case BoxControlPoint.LeftBottom:
      deltaX = left - x;
      deltaY = y - bottom;
      break;
    case BoxControlPoint.LeftCenter:
      deltaX = left - x;
      break;
    default:
      break;
  }

  return { x: deltaX, y: deltaY };
}

export function getBoxControlPointCursor(cp: BoxControlPoint): CursorTypes {
  let cursor = CursorTypes.Default;

  switch (cp) {
    case BoxControlPoint.LeftTop:
      cursor = CursorTypes.ResizeNWSE;
      break;
    case BoxControlPoint.CenterTop:
      cursor = CursorTypes.ResizeNorthSouth;
      break;
    case BoxControlPoint.RightTop:
      cursor = CursorTypes.ResizeNESW;
      break;
    case BoxControlPoint.RightCenter:
      cursor = CursorTypes.ResizeEastWest;
      break;
    case BoxControlPoint.RightBottom:
      cursor = CursorTypes.ResizeNWSE;
      break;
    case BoxControlPoint.CenterBottom:
      cursor = CursorTypes.ResizeNorthSouth;
      break;
    case BoxControlPoint.LeftBottom:
      cursor = CursorTypes.ResizeNESW;
      break;
    case BoxControlPoint.LeftCenter:
      cursor = CursorTypes.ResizeEastWest;
      break;
    case BoxControlPoint.None:
      cursor = CursorTypes.Default;
      break;
    default:
      break;
  }

  return cursor;
}

/* Line (and Arrow) Control Points */

export interface LineControlPoint {
  pointIndex: number;
}

export function lineControlPointHit(
  mc: Markup,
  hit: Carn.Hit
): LineControlPoint | null {
  if (!mc.mkMarkup) {
    return null;
  }

  const points = mc.mkMarkup.getPoints() as Point[];

  for (let pointIndex = 0; pointIndex < points.length; pointIndex++) {
    const point = points[pointIndex];
    if (pointInCircle(point.x, point.y, hit.x, hit.y, CONTROL_POINT_RADIUS)) {
      return { pointIndex };
    }
  }

  return null;
}
